import { createShowcaseProjects, createShowcaseUsers } from './showcaseCatalog';
import { getProjectLinkedCompanies } from './demoStore';
import {
  isProjectAssignmentRole,
  type ProjectAssignmentRole,
} from '@/types/projectParticipants';

export type DemoParticipantStatus = 'active' | 'suspended' | 'revoked';

export interface DemoProjectParticipantRecord {
  id: string;
  projectId: string;
  userId: string;
  projectRole: ProjectAssignmentRole;
  companyId: string;
  status: DemoParticipantStatus;
  name: string;
  email?: string;
  avatarUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DemoCompanyRepresentativeRecord {
  id: string;
  projectId: string;
  userId: string;
  companyId: string;
  status: DemoParticipantStatus;
  createdAt: string;
  updatedAt: string;
}

export interface DemoParticipantNotification {
  id: string;
  userId: string;
  actorId: string | null;
  type:
    | 'project_participant_connected'
    | 'project_invitation_accepted'
    | 'project_role_changed'
    | 'decision_requested'
    | 'decision_recorded'
    | 'decision_action_assigned';
  title: string;
  body: string;
  projectId: string;
  companyId?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  readAt: string | null;
  dismissedAt: string | null;
  archivedAt: string | null;
}

export interface AssignDemoParticipantInput {
  projectId: string;
  userId: string;
  projectRole: ProjectAssignmentRole;
  companyId: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
}

const SEEDED_AT = '2026-07-20T10:00:00.000Z';
const listeners = new Set<() => void>();

function buildInitialParticipants(): DemoProjectParticipantRecord[] {
  const users = new Map(createShowcaseUsers().map(user => [user.id, user]));
  const seedAssignments = [
    {
      userId: 'cbfc74ff-8622-4227-a560-855e16a45e52',
      projectRole: 'Project Manager',
      companyRelationship: 'internal',
    },
    { userId: '5', projectRole: 'Contributor', companyRelationship: 'vendor' },
    { userId: '8', projectRole: 'Observer', companyRelationship: 'investor' },
    {
      userId: '532e3ecc-2e17-4be5-a27a-8cd1e47a05a6',
      projectRole: 'Observer',
      companyRelationship: 'capital_recipient',
    },
    { userId: '7', projectRole: 'Observer', companyRelationship: 'partner' },
  ] as const;

  const seeded: DemoProjectParticipantRecord[] = createShowcaseProjects().flatMap(project => seedAssignments.flatMap(seat => {
    const companyLink = getProjectLinkedCompanies(project.id)
      .find(link => link.relationship_type === seat.companyRelationship);
    if (!companyLink) return [];
    const user = users.get(seat.userId);
    return [{
      id: `showcase-participant-${project.id}-${seat.userId}`,
      projectId: project.id,
      userId: seat.userId,
      projectRole: seat.projectRole,
      companyId: companyLink.company_id,
      status: 'active' as const,
      name: user?.name ?? 'Unknown',
      email: user?.email,
      avatarUrl: user?.avatar,
      createdAt: SEEDED_AT,
      updatedAt: SEEDED_AT,
    }];
  }));
  // One explicit delivery seat for the Investee preview, not workspace-wide authority.
  const investee = users.get('21');
  const company = getProjectLinkedCompanies('2').find(link =>
    link.company_id === 'showcase-company-partner' && link.status === 'active');
  if (investee && company) seeded.push({
    id: 'showcase-participant-2-21', projectId: '2', userId: investee.id,
    projectRole: 'Contributor', companyId: company.company_id, status: 'active',
    name: investee.name, email: investee.email, avatarUrl: investee.avatar,
    createdAt: SEEDED_AT, updatedAt: SEEDED_AT,
  });
  return seeded;
}

let participants = buildInitialParticipants();
let representatives: DemoCompanyRepresentativeRecord[] = participants.map(participant => ({
  id: `showcase-representative-${participant.projectId}-${participant.userId}`,
  projectId: participant.projectId,
  userId: participant.userId,
  companyId: participant.companyId,
  status: participant.status,
  createdAt: participant.createdAt,
  updatedAt: participant.updatedAt,
}));
let notifications: DemoParticipantNotification[] = [];

function notify(): void {
  listeners.forEach(listener => listener());
}

function assertLinkedCompany(projectId: string, companyId: string): void {
  const isLinked = getProjectLinkedCompanies(projectId)
    .some(link => link.company_id === companyId && link.status === 'active');
  if (!isLinked) throw new Error('Select a company linked to this project.');
}

export function subscribeDemoProjectParticipants(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getDemoProjectParticipants(
  projectId: string,
  options?: { includeInactive?: boolean },
): DemoProjectParticipantRecord[] {
  return participants
    .filter(participant => participant.projectId === projectId
      && (options?.includeInactive || participant.status === 'active'))
    .map(participant => ({ ...participant }));
}

export function getDemoProjectParticipant(
  projectId: string,
  userId: string,
): DemoProjectParticipantRecord | undefined {
  const participant = participants.find(item => item.projectId === projectId && item.userId === userId);
  return participant ? { ...participant } : undefined;
}

export function getDemoCompanyRepresentatives(projectId?: string): DemoCompanyRepresentativeRecord[] {
  return representatives
    .filter(item => (!projectId || item.projectId === projectId) && item.status === 'active')
    .map(item => ({ ...item }));
}

export function assignDemoProjectParticipant(
  input: AssignDemoParticipantInput,
): { action: 'added' | 'updated' | 'unchanged'; participant: DemoProjectParticipantRecord } {
  if (!isProjectAssignmentRole(input.projectRole)) {
    throw new Error('Select Project Manager, Contributor, or Observer.');
  }
  assertLinkedCompany(input.projectId, input.companyId);

  const now = new Date().toISOString();
  const index = participants.findIndex(item => item.projectId === input.projectId && item.userId === input.userId);
  const current = index >= 0 ? participants[index] : undefined;
  const unchanged = current?.status === 'active'
    && current.projectRole === input.projectRole
    && current.companyId === input.companyId;

  const participant: DemoProjectParticipantRecord = {
    id: current?.id ?? `demo-participant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    projectId: input.projectId,
    userId: input.userId,
    projectRole: input.projectRole,
    companyId: input.companyId,
    status: 'active',
    name: input.name ?? current?.name ?? 'Unknown',
    email: input.email ?? current?.email,
    avatarUrl: input.avatarUrl ?? current?.avatarUrl,
    createdAt: current?.createdAt ?? now,
    updatedAt: now,
  };

  if (index >= 0) participants[index] = participant;
  else participants.push(participant);

  const representativeIndex = representatives.findIndex(item =>
    item.projectId === input.projectId && item.userId === input.userId);
  const representative: DemoCompanyRepresentativeRecord = {
    id: representatives[representativeIndex]?.id
      ?? `demo-representative-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    projectId: input.projectId,
    userId: input.userId,
    companyId: input.companyId,
    status: 'active',
    createdAt: representatives[representativeIndex]?.createdAt ?? now,
    updatedAt: now,
  };
  if (representativeIndex >= 0) representatives[representativeIndex] = representative;
  else representatives.push(representative);

  notify();
  return { action: unchanged ? 'unchanged' : current ? 'updated' : 'added', participant: { ...participant } };
}

export function changeDemoProjectParticipantRole(
  projectId: string,
  userId: string,
  projectRole: ProjectAssignmentRole,
): { action: 'updated' | 'unchanged'; oldRole: ProjectAssignmentRole; participant: DemoProjectParticipantRecord } {
  if (!isProjectAssignmentRole(projectRole)) {
    throw new Error('Select Project Manager, Contributor, or Observer.');
  }
  const index = participants.findIndex(item => item.projectId === projectId && item.userId === userId && item.status === 'active');
  if (index < 0) throw new Error('Project participant was not found.');
  const current = participants[index];
  if (current.projectRole === projectRole) {
    return { action: 'unchanged', oldRole: current.projectRole, participant: { ...current } };
  }
  if (current.projectRole === 'Project Manager' && projectRole !== 'Project Manager') {
    const managerCount = participants.filter(item => item.projectId === projectId
      && item.projectRole === 'Project Manager' && item.status === 'active').length;
    if (managerCount <= 1) throw new Error('The project must retain at least one Project Manager.');
  }

  const oldRole = current.projectRole;
  const updated = { ...current, projectRole, updatedAt: new Date().toISOString() };
  participants[index] = updated;
  notify();
  return { action: 'updated', oldRole, participant: { ...updated } };
}

export function setDemoProjectParticipantStatus(
  projectId: string,
  userId: string,
  status: DemoParticipantStatus,
): { oldRole: ProjectAssignmentRole; participant: DemoProjectParticipantRecord } | null {
  const index = participants.findIndex(item => item.projectId === projectId && item.userId === userId);
  if (index < 0) return null;
  const current = participants[index];
  if (status !== 'active' && current.status === 'active' && current.projectRole === 'Project Manager') {
    const managerCount = participants.filter(item => item.projectId === projectId
      && item.projectRole === 'Project Manager' && item.status === 'active').length;
    if (managerCount <= 1) throw new Error('The project must retain at least one Project Manager.');
  }
  const updated = { ...current, status, updatedAt: new Date().toISOString() };
  participants[index] = updated;
  const representativeIndex = representatives.findIndex(item => item.projectId === projectId && item.userId === userId);
  if (representativeIndex >= 0) {
    representatives[representativeIndex] = {
      ...representatives[representativeIndex],
      status,
      updatedAt: updated.updatedAt,
    };
  }
  notify();
  return { oldRole: current.projectRole, participant: { ...updated } };
}

export function createDemoParticipantNotification(
  input: Omit<DemoParticipantNotification, 'id' | 'createdAt' | 'readAt' | 'dismissedAt' | 'archivedAt'>,
): DemoParticipantNotification {
  const notification: DemoParticipantNotification = {
    ...input,
    id: `demo-project-notification-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    readAt: null,
    dismissedAt: null,
    archivedAt: null,
  };
  notifications.unshift(notification);
  notify();
  return { ...notification };
}

export function getDemoParticipantNotifications(userId?: string): DemoParticipantNotification[] {
  if (!userId) return [];
  return notifications
    .filter(item => item.userId === userId && !item.dismissedAt && !item.archivedAt)
    .map(item => ({ ...item, metadata: { ...item.metadata } }));
}

export function updateDemoParticipantNotification(
  notificationId: string,
  userId: string,
  updates: Pick<Partial<DemoParticipantNotification>, 'readAt' | 'dismissedAt' | 'archivedAt'>,
): boolean {
  const index = notifications.findIndex(item => item.id === notificationId && item.userId === userId);
  if (index < 0) return false;
  notifications[index] = { ...notifications[index], ...updates };
  notify();
  return true;
}

export function resetDemoProjectParticipantStore(): void {
  participants = buildInitialParticipants();
  representatives = participants.map(participant => ({
    id: `showcase-representative-${participant.projectId}-${participant.userId}`,
    projectId: participant.projectId,
    userId: participant.userId,
    companyId: participant.companyId,
    status: participant.status,
    createdAt: participant.createdAt,
    updatedAt: participant.updatedAt,
  }));
  notifications = [];
  notify();
}
